// Shared geometry + palette constants for the WorldMap and its subcomponents.
import { geoEquirectangular, geoPath } from "d3-geo";

export const W = 900;
export const H = 460;

// ONE projection shared by the country topology AND the city markers so they align pixel-perfect.
export const PROJECTION = geoEquirectangular()
  .scale(W / (2 * Math.PI))
  .translate([W / 2, H / 2]);
export const PATH_GEN = geoPath(PROJECTION);

// Universal `project(lat, lng)` — d3 uses [lng, lat] order.
export const project = (lat, lng) => {
  const p = PROJECTION([lng, lat]);
  return [p[0], p[1]];
};

export const PLANET_COLORS = {
  Jupiter: "#C8A96E",
  Sun: "#FFB020",
  Moon: "#9CBF8E",
  Venus: "#C9744F",
  Chiron: "#14B8A6",
  AC: "#38BDF8",
  IC: "#9E8BB0",
  MC: "#F97316",
  DC: "#F472B6",
};

export const LINE_ORDER = ["Jupiter", "Sun", "Moon", "Venus", "Chiron", "AC", "IC", "MC", "DC"];
export const LINE_KIND = {
  Jupiter: "planet", Sun: "planet", Moon: "planet", Venus: "planet", Chiron: "planet",
  AC: "angle", IC: "angle", MC: "angle", DC: "angle",
};

export const MAJOR_CITIES = [
  // Asia
  ["Tokyo", 35.68, 139.65], ["Delhi", 28.61, 77.20], ["Shanghai", 31.23, 121.47],
  ["Mumbai", 19.08, 72.88], ["Beijing", 39.90, 116.41], ["Dhaka", 23.81, 90.41],
  ["Osaka", 34.69, 135.50], ["Karachi", 24.86, 67.01], ["Manila", 14.60, 120.98],
  ["Bangalore", 12.97, 77.59], ["Jakarta", -6.21, 106.85], ["Bangkok", 13.76, 100.50],
  ["Seoul", 37.57, 126.98], ["Tehran", 35.69, 51.39], ["Kuala Lumpur", 3.14, 101.69],
  ["Hong Kong", 22.32, 114.17], ["Riyadh", 24.71, 46.68], ["Baghdad", 33.31, 44.36],
  ["Singapore", 1.35, 103.82], ["Dubai", 25.20, 55.27], ["Kolkata", 22.57, 88.36],
  ["Chennai", 13.08, 80.27], ["Hyderabad", 17.39, 78.49], ["Lahore", 31.55, 74.34],
  ["Islamabad", 33.68, 73.05], ["Kyoto", 35.01, 135.77], ["Taipei", 25.03, 121.57],
  ["Hanoi", 21.03, 105.85], ["Ho Chi Minh City", 10.82, 106.63], ["Yangon", 16.87, 96.20],
  ["Colombo", 6.93, 79.86], ["Kathmandu", 27.72, 85.32], ["Ulaanbaatar", 47.89, 106.91],
  ["Doha", 25.29, 51.53], ["Abu Dhabi", 24.47, 54.37], ["Tel Aviv", 32.08, 34.78],
  ["Jerusalem", 31.78, 35.22], ["Beirut", 33.89, 35.51], ["Amman", 31.95, 35.93],
  // Europe
  ["Istanbul", 41.01, 28.98], ["Moscow", 55.75, 37.62], ["Paris", 48.85, 2.35],
  ["London", 51.51, -0.13], ["Madrid", 40.42, -3.70], ["Berlin", 52.52, 13.41],
  ["Rome", 41.90, 12.50], ["Vienna", 48.21, 16.37], ["Amsterdam", 52.37, 4.90],
  ["Barcelona", 41.39, 2.17], ["Lisbon", 38.72, -9.14], ["Warsaw", 52.23, 21.01],
  ["Prague", 50.08, 14.44], ["Athens", 37.98, 23.73], ["Kyiv", 50.45, 30.52],
  ["Stockholm", 59.33, 18.07], ["Oslo", 59.91, 10.75], ["Helsinki", 60.17, 24.94],
  ["Copenhagen", 55.68, 12.57], ["Dublin", 53.35, -6.26], ["Brussels", 50.85, 4.35],
  ["Budapest", 47.50, 19.04], ["Bucharest", 44.43, 26.10], ["Reykjavik", 64.13, -21.82],
  ["Zurich", 47.37, 8.55], ["Milan", 45.46, 9.19], ["St Petersburg", 59.93, 30.34],
  // Americas
  ["São Paulo", -23.55, -46.63], ["Mexico City", 19.43, -99.13],
  ["New York", 40.71, -74.01], ["Buenos Aires", -34.60, -58.38],
  ["Rio de Janeiro", -22.91, -43.17], ["Los Angeles", 34.05, -118.24],
  ["Chicago", 41.88, -87.63], ["Santiago", -33.45, -70.66], ["Toronto", 43.65, -79.38],
  ["Bogotá", 4.71, -74.07], ["Lima", -12.05, -77.04], ["Havana", 23.11, -82.35],
  ["San Francisco", 37.77, -122.42], ["Vancouver", 49.28, -123.12],
  ["Montreal", 45.50, -73.57], ["Washington", 38.91, -77.04],
  ["Miami", 25.76, -80.19], ["Boston", 42.36, -71.06], ["Seattle", 47.61, -122.33],
  ["Houston", 29.76, -95.37], ["Atlanta", 33.75, -84.39], ["Denver", 39.74, -104.99],
  ["Las Vegas", 36.17, -115.14], ["Caracas", 10.48, -66.90], ["Quito", -0.18, -78.47],
  // Africa
  ["Cairo", 30.04, 31.24], ["Lagos", 6.52, 3.38], ["Kinshasa", -4.44, 15.27],
  ["Nairobi", -1.29, 36.82], ["Cape Town", -33.92, 18.42], ["Johannesburg", -26.20, 28.05],
  ["Casablanca", 33.57, -7.59], ["Marrakech", 31.63, -7.99], ["Addis Ababa", 9.03, 38.74],
  ["Accra", 5.60, -0.19], ["Dakar", 14.71, -17.47], ["Algiers", 36.75, 3.05],
  ["Tunis", 36.80, 10.18], ["Khartoum", 15.50, 32.56],
  // Oceania
  ["Sydney", -33.87, 151.21], ["Melbourne", -37.81, 144.96], ["Brisbane", -27.47, 153.03],
  ["Perth", -31.95, 115.86], ["Auckland", -36.85, 174.76], ["Wellington", -41.29, 174.78],
  ["Honolulu", 21.31, -157.86],
  // Additional mega-cities (Aug 2026 top-population expansion)
  ["Tianjin", 39.14, 117.18], ["Dongguan", 23.02, 113.75], ["Nanjing", 32.06, 118.78],
  ["Hangzhou", 30.29, 120.16], ["Foshan", 23.03, 113.13], ["Qingdao", 36.06, 120.38],
  ["Shenyang", 41.79, 123.43], ["Suzhou", 31.30, 120.60], ["Ahmedabad", 23.03, 72.59],
  ["Abidjan", 5.35, -4.00], ["Dar es Salaam", -6.82, 39.27], ["Alexandria", 31.20, 29.92],
  ["Harbin", 45.75, 126.65], ["Dalian", 38.91, 121.60], ["Kano", 12.00, 8.52],
  ["Peshawar", 34.01, 71.58], ["Changchun", 43.88, 125.32], ["Jeddah", 21.49, 39.19],
  ["Surat", 21.20, 72.83], ["Kabul", 34.53, 69.17], ["Giza", 30.01, 31.21],
  ["Jinan", 36.67, 117.00], ["Zhengzhou", 34.76, 113.65], ["Bamako", 12.61, -7.98],
  ["New Taipei", 25.06, 121.46], ["Chattogram", 22.34, 91.83], ["Kunming", 25.04, 102.72],
  ["Nanning", 22.82, 108.32], ["Faisalabad", 31.42, 73.09], ["Yokohama", 35.43, 139.65],
  ["Fuzhou", 26.06, 119.31], ["Ningbo", 29.88, 121.55], ["Ibadan", 7.38, 3.91],
];

export const CAPITALS = {
  "United States of America": "Washington, D.C.", "Canada": "Ottawa", "Mexico": "Mexico City",
  "Brazil": "Brasília", "Argentina": "Buenos Aires", "Chile": "Santiago", "Peru": "Lima",
  "Colombia": "Bogotá", "Venezuela": "Caracas", "Bolivia": "La Paz", "Uruguay": "Montevideo",
  "Ecuador": "Quito", "Paraguay": "Asunción", "Cuba": "Havana",
  "United Kingdom": "London", "France": "Paris", "Germany": "Berlin", "Spain": "Madrid",
  "Portugal": "Lisbon", "Italy": "Rome", "Netherlands": "Amsterdam", "Belgium": "Brussels",
  "Ireland": "Dublin", "Norway": "Oslo", "Sweden": "Stockholm", "Finland": "Helsinki",
  "Denmark": "Copenhagen", "Iceland": "Reykjavík", "Poland": "Warsaw", "Austria": "Vienna",
  "Switzerland": "Bern", "Czechia": "Prague", "Greece": "Athens", "Turkey": "Ankara",
  "Russia": "Moscow", "Ukraine": "Kyiv", "Romania": "Bucharest", "Hungary": "Budapest",
  "China": "Beijing", "Japan": "Tokyo", "South Korea": "Seoul", "North Korea": "Pyongyang",
  "India": "New Delhi", "Pakistan": "Islamabad", "Bangladesh": "Dhaka", "Nepal": "Kathmandu",
  "Thailand": "Bangkok", "Vietnam": "Hanoi", "Indonesia": "Jakarta", "Philippines": "Manila",
  "Malaysia": "Kuala Lumpur", "Singapore": "Singapore", "Cambodia": "Phnom Penh",
  "Myanmar": "Naypyidaw", "Laos": "Vientiane", "Mongolia": "Ulaanbaatar", "Sri Lanka": "Colombo",
  "Egypt": "Cairo", "Morocco": "Rabat", "Algeria": "Algiers", "Tunisia": "Tunis",
  "Libya": "Tripoli", "Sudan": "Khartoum", "Ethiopia": "Addis Ababa", "Kenya": "Nairobi",
  "Nigeria": "Abuja", "Ghana": "Accra", "South Africa": "Pretoria", "Tanzania": "Dodoma",
  "Uganda": "Kampala", "Senegal": "Dakar", "Madagascar": "Antananarivo",
  "Saudi Arabia": "Riyadh", "United Arab Emirates": "Abu Dhabi", "Iran": "Tehran",
  "Iraq": "Baghdad", "Israel": "Jerusalem", "Jordan": "Amman", "Lebanon": "Beirut",
  "Syria": "Damascus", "Qatar": "Doha", "Kuwait": "Kuwait City", "Oman": "Muscat",
  "Yemen": "Sana'a", "Afghanistan": "Kabul",
  "Australia": "Canberra", "New Zealand": "Wellington", "Papua New Guinea": "Port Moresby",
  "Fiji": "Suva",
  "Kazakhstan": "Astana", "Uzbekistan": "Tashkent", "Azerbaijan": "Baku", "Georgia": "Tbilisi",
  "Armenia": "Yerevan",
};
